import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Spinner } from "react-bootstrap";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";

import makeAnimated from "react-select/animated";

import commoncss from "../../css/common.module.css";
import { ReactComponent as PlusIcon } from "../../assets/icons/AddPlus.svg";
import { ReactComponent as CirclePlus } from "../../assets/icons/circlePlus.svg";
import { ReactComponent as AddEditor } from "../../assets/icons/addEditor.svg";
import { ReactComponent as AddTable } from "../../assets/icons/addTable.svg";
import libcss from "../../css/library.module.css";
import MenuIcon from "../../assets/icons/Menu.png";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import {
  createCashflowApiHandler,
  createSubCashflowApiHandler,
  updateCashflowApiHandler,
  updateSubCashflowApiHandler,
  deleteCashflowApiHandler,
  deleteSubCashflowApiHandler,
  setCashflow,
  deleteCashflow,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";

import { ReactComponent as EditIcon } from "../../../myfin_v2/assets/icons/editPri.svg";
import { ReactComponent as CloseIcon } from "../../../myfin_v2/assets/icons/close.svg";
import DeletePopup from "../../elements/DeletePopup";
import SuccessPopup from "../../elements/SuccessPopup";
import { ReactComponent as DelIcon } from "../../../myfin_v2/assets/icons/delete_red.svg";

import TemplateTableGenerator from "./TemplateTableGenerator";
import SidePanel from "./SidePanel.jsx";

import { getTemplateDetailsApiHandler } from "../../../WorkSpace/Components/ApiServices/apiHandler";

import QuillReactEditor from "../../components/QuillReactEditor";

const genId = () => Math.random().toString(36).slice(2);

const normalizeCashflowLinkCol = (col) => {
  const normalized = String(col || "").trim().toLowerCase();
  if (["cy", "current year", "currentyear"].includes(normalized)) {
    return "currentYear";
  }
  if (["py", "previous year", "previousyear"].includes(normalized)) {
    return "previousYear";
  }
  return col;
};

const buildCashflowLinkKeys = (tableId, row, col) => {
  if (!tableId || !row || !col) return [];

  const rowValue = String(row).trim();
  const normalizedCol = normalizeCashflowLinkCol(col);

  return [
    `${tableId}-${rowValue}-${col}`,
    `${tableId}-${rowValue}-${normalizedCol}`,
  ];
};

const findCashflowOwnerIdByTableId = (cashflowNotes, tableId) => {
  if (!Array.isArray(cashflowNotes) || !tableId) return null;

  for (const note of cashflowNotes) {
    const blocks = note?.data || [];
    const hasMatchingTable = blocks.some(
      (block) => block?.dataType === "table" && block?._id === tableId
    );

    if (hasMatchingTable) {
      return note?._id || null;
    }

    const subNoteOwnerId = findCashflowOwnerIdByTableId(note?.subNotes || [], tableId);
    if (subNoteOwnerId) {
      return subNoteOwnerId;
    }
  }

  return null;
};

const findCashflowTableById = (cashflowNotes, tableId) => {
  if (!Array.isArray(cashflowNotes) || !tableId) return null;

  for (const note of cashflowNotes) {
    const matchedBlock = (note?.data || []).find(
      (block) => block?.dataType === "table" && (block?._id === tableId || block?.id === tableId)
    );

    if (matchedBlock) {
      return matchedBlock;
    }

    const subNoteMatch = findCashflowTableById(note?.subNotes || [], tableId);
    if (subNoteMatch) {
      return subNoteMatch;
    }
  }

  return null;
};

const findCashflowNoteById = (cashflowNotes, noteId) => {
  if (!Array.isArray(cashflowNotes) || !noteId) return null;
  for (const note of cashflowNotes) {
    if (note?._id === noteId) return note;
    const nested = findCashflowNoteById(note?.subNotes, noteId);
    if (nested) return nested;
  }
  return null;
};

// Normalize your BS Note3 schema into a flat array of items
const normalizeFromInitial = (initialDoc) => {
  const items = [];
  (initialDoc?.data || []).forEach((block) => {
    if (block.dataType === "text") {
      items.push({
        id: block._id || genId(),
        _id: block._id,
        type: "text",
        dataType: "text",
        text: Array.isArray(block.text)
          ? block.text.map((t) => ({ ...t, id: t.id || genId() }))
          : [
              {
                id: genId(),
                content: block?.textRaw || block?.text?.[0]?.content || " ",
              },
            ],
      });
    } else if (block.dataType === "table") {
      const tableRow = Array.isArray(block.tableRow)
        ? block.tableRow.map((r) => ({ ...r }))
        : [];
      const tableColumn = Array.isArray(block.tableColumn)
        ? block.tableColumn.map((c) => ({ ...c }))
        : [];

      let tableData = null;
      if (Array.isArray(block?.table) && block.table.length === tableRow.length && tableRow.length > 0) {
        tableData = block.table.map((row, idx) => ({
          ...row,
          _id: row?._id || tableRow[idx]?._id,
          name: row.name || tableRow[idx]?.name || "",
          isSubRow: row.isSubRow || row.issubrow || tableRow[idx]?.isSubRow || false,
        }));
      } else if (tableRow.length > 0) {
        tableData = tableRow.map((r, idx) => {
          const existingRow = Array.isArray(block?.table) ? block.table[idx] : null;
          return {
            ...(existingRow || {}),
            _id: existingRow?._id || r?._id,
            name: r.name || existingRow?.name || "",
            currentYear: existingRow?.currentYear ?? "",
            previousYear: existingRow?.previousYear ?? "",
            isSubRow: r.isSubRow || r.issubrow || false,
          };
        });
      }

      items.push({
        id: block._id || genId(),
        _id: block._id,
        type: "table",
        tableTitle: block.tableTitle || "Untitled Table",
        tableRow,
        tableColumn,
        tableType: block?.tableType ?? "table",
        tableData,
        hasCellData: true,
        cellFormulas: Array.isArray(block?.cellFormulas)
          ? block.cellFormulas.map((f) => ({ ...f }))
          : [],
      });
    }
  });
  return items;
};

// Serialize the flat list back to your exact schema
const serialize = (name, items, { includeRowIds = false } = {}) => {
  const data = (items || [])
    .map((it) => {
      if (it.type === "text" || it.dataType === "text") {
        const textArray =
          Array.isArray(it.text) && it.text.length > 0
            ? it.text.map((t) => ({
                content: typeof t === "string" ? t : (t?.content ?? ""),
              }))
            : [
                {
                  content: it.textRaw || it.textPlain || "",
                },
              ];

        return {
          dataType: "text",
          text: textArray,
        };
      }

      if (it.type === "table" || it.dataType === "table") {
        const tableRow = (it.tableRow || []).map((r) => {
          const rowObj = {
            name: r.name || "",
            lineType: r.lineType === "formula" ? "formula" : "line item",
          };

          if (includeRowIds && r._id) rowObj._id = r._id;

          if (r.formulaType && ["total", "subtotal"].includes(r.formulaType)) {
            rowObj.formulaType = r.formulaType;
          }

          if (typeof r.isBold === "boolean") {
            rowObj.isBold = r.isBold;
          }

          if (rowObj.lineType === "formula") {
            rowObj.formula = Array.isArray(r.formula)
              ? r.formula.map((f) => ({
                  name: f.name || "",
                  sign: f.sign === "negative" ? "negative" : "positive",
                }))
              : [];
          }

          return rowObj;
        });

        const tableColumn = (it.tableColumn || []).map((c) => {
          const colObj = {
            name: c.name || "",
            lineType: c.lineType === "formula" ? "formula" : "line item",
          };

          if (colObj.lineType === "formula") {
            colObj.formula = Array.isArray(c.formula)
              ? c.formula.map((f) => ({
                  name: f.name || "",
                  sign: f.sign === "negative" ? "negative" : "positive",
                }))
              : [];
          }

          return colObj;
        });

        const rawTableData = it.tableData || it.table || [];
        const table = Array.isArray(rawTableData)
          ? rawTableData.map((row) => {
              if (typeof row !== "object" || row === null) return {};
              const cleanRow = {};
              Object.keys(row).forEach((key) => {
                const lowerKey = key.toLowerCase();
                if (
                  key === "_id" ||
                  key === "id" ||
                  lowerKey === "isnewrow" ||
                  lowerKey === "isnew" ||
                  lowerKey === "isedited"
                ) {
                  return;
                }
                cleanRow[key] = row[key];
              });
              return cleanRow;
            })
          : [];

        const tableObj = {
          dataType: "table",
          tableTitle: it.tableTitle || "Untitled Table",
          tableRow,
          tableColumn,
          table,
          cellFormulas: Array.isArray(it.cellFormulas)
            ? it.cellFormulas.map((formula) => ({ ...formula }))
            : [],
        };

        return tableObj;
      }

      return null;
    })
    .filter(Boolean);

  const payload = { data };
  if (name !== undefined && name !== null && name !== "") {
    payload.name = name;
  }

  return payload;
};

export default function Cashflow({
  tempId,
  setHandleSave,
  handleSave,
  isReadOnly = false,
  isConsol = false,
  consolID,
}) {
  const [name, setName] = useState("");
  const [items, setItems] = useState(() => normalizeFromInitial(null));
  const [output, setOutput] = useState(null);
  const [forceOpenTableId, setForceOpenTableId] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const animatedComponents = makeAnimated();
  let history = useHistory();
  const dispatch = useDispatch();
  const [libraryName, setLibraryName] = useState("ST Library");
  const [activeItem, setActiveItem] = useState(null);
  const [selNote, setSelNote] = useState(null);
  const [selNoteId, setSelNoteId] = useState(null);
  const [selParentId, setSelParentId] = useState(null);
  const [notes, setNotes] = useState(null);
  const [note, setNote] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isCashflowSaving, setIsCashflowSaving] = useState(false);
  const pendingCashflowSavesRef = useRef(0);
  const beginCashflowSave = () => {
    pendingCashflowSavesRef.current += 1;
    setIsCashflowSaving(true);
  };
  const endCashflowSave = () => {
    pendingCashflowSavesRef.current = Math.max(0, pendingCashflowSavesRef.current - 1);
    setIsCashflowSaving(pendingCashflowSavesRef.current > 0);
  };

  const handleConfirmDelete = () => {
    if (!rowToDelete) return;
    const { tableId, rowIndex } = rowToDelete;
    setRowToDelete(null);

    const filteredItems = items.map((item) => {
      if (item.id !== tableId && item._id !== tableId) return item;

      const tableData = [...(item.tableData || item.table || [])];
      const tableRow = [...(item.tableRow || [])];
      tableData.splice(rowIndex - 1, 1);
      tableRow.splice(rowIndex - 1, 1);

      return { ...item, tableData, table: tableData, tableRow };
    });

    const payload = serialize(name, filteredItems);
    const apiCall = selNote === "general_sn"
      ? updateSubCashflowApiHandler(
          {
            groupingTemplateID: templateData?._id || tempId,
            cashflowID: selParentId,
            subNoteID: selNoteId,
          },
          payload
        )
      : updateCashflowApiHandler(
          { groupingTemplateID: templateData?._id || tempId, cashflowID: selNoteId },
          payload
        );

    apiCall
      .then((response) => {
        if (response?.data?.success) {
          setItems(filteredItems);
          getTemplateDetails();
          setSuccessMessage("Your line item has been deleted!");
          setShowSuccessPopup(true);
        } else {
          swal(
            "Error",
            response?.data?.error || "Unable to delete the row",
            "error"
          );
        }
      })
      .catch((error) => {
        swal(
          "Error",
          error?.response?.data?.error || "Unable to delete the row",
          "error"
        );
      });
  };

  const [isComposing, setIsComposing] = useState(false);
  const [noteName, setNoteName] = useState("");
  const inputRef = useRef(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingSubIndex, setEditingSubIndex] = useState(null);
  const [editValue, setEditValue] = useState("");
  const isDeletingRef = useRef(false);

  const [showPanel, setShowPanel] = useState(false);
  const [activeCellKey, setActiveCellKey] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [linkedCells, setLinkedCells] = useState({});
  const [activeTableId, setActiveTableId] = useState(null);
  const [activeRawTableId, setActiveRawTableId] = useState(null);

  const currentNote = (() => {
    if (!templateData?.cashflow || !selNoteId) return null;
    let found = null;
    const search = (arr) => {
      for (const n of arr) {
        if (n._id === selNoteId) {
          found = n;
          return;
        }
        if (n.subNotes) search(n.subNotes);
      }
    };
    search(templateData.cashflow);
    return found;
  })();

  const currentTableBlock = currentNote?.data?.find((b) => b.dataType === "table");

  const resolvedDataId = activeTableId || currentTableBlock?._id;

  const resolvedCashflowId = currentNote?._id || selNoteId;

  const activeTable =
    findCashflowTableById(templateData?.cashflow, resolvedDataId) ||
    currentTableBlock ||
    (items || []).find((it) => it._id === activeTableId || it.id === activeTableId);

const onCellLinkClick = (tableId, r, c, tableRow, currentRowName) => {
  const rowName = (currentRowName && String(currentRowName).trim()) || tableRow?.[r - 1]?.name || String(r);
  const colHeader = c === 1 ? "currentYear" : "previousYear";

  setSelectedCell({ row: rowName, col: colHeader });
  setActiveTableId(tableId);
  setActiveRawTableId(tableId);
  setActiveCellKey(`${tableId}-${r}-${c}`);
  setShowPanel(true);
};

  const handleApply = (data, updatedTemplate) => {
    setLinkedCells((prev) => {
      const updated = { ...prev };
      if (!data) {
        delete updated[activeCellKey];
      } else {
        updated[activeCellKey] = data;
      }
      return updated;
    });

    if (updatedTemplate) {
      setTemplateData(updatedTemplate);
    } else {
      getTemplateDetails();
    }
  };

  const getTemplateDetails = (updatedTemplate) => {
    if (updatedTemplate && updatedTemplate.cashflow) {
      setTemplateData(updatedTemplate);
      return;
    }
    getTemplateDetailsApiHandler({ groupingTemplateID: tempId })
      .then((response) => {
        if (response?.data?.success === true) {
          setTemplateData(response?.data?.groupingTemplate);
        }
      })
      .catch((error) => {});
  };

  useEffect(() => {
    getTemplateDetails();
  }, []);

  useEffect(() => {
    getTemplateDetails();
  }, [activeItem]);

 useEffect(() => {
  if (!showPanel) return;
  if (activeTableId) return;
  setShowPanel(false);
  swal("Error", "Table ID is missing.", "error");
}, [showPanel, activeTableId]);

  useEffect(() => {
    if (notes && notes.length > 0 && !selNote) {
      const firstNote = notes[0];
      const initialItems = normalizeFromInitial(firstNote);
      setItems(initialItems);
      setActiveItem(`gs_0`);
      setSelNote("general");
      setSelNoteId(firstNote._id);
      setSelParentId(null);
      setOutput(serialize(firstNote.name, initialItems));
      setName(firstNote.name || "");
    }
  }, [notes, selNote]);

  const [isAddingTable, setIsAddingTable] = useState(false);

  const getResolvedTableId = (item) => item?._id || item?.id;

  const handleAddParagraph = () => {
    setItems((prev) => [
      ...prev,
      {
        id: genId(),
        type: "text",
        text: [{ id: genId(), content: "" }],
        textFormat: "html",
        textRaw: " ",
        textPlain: " ",
      },
    ]);
  };

  const handleAddTable = () => {
    setIsAddingTable(true);
  };

  const removeItem = (id) => {
    const target = items.find((it) => it.id === id);
    if (!target) return;

    if (!templateData?._id || !selNoteId) {
      swal("Error", "Template data not loaded yet", "error");
      return;
    }

    setDeleteConfirm({
      title: "Are you sure?",
      message: "This item will be deleted permanently",
      onConfirm: () => {
        setDeleteConfirm(null);
        const filteredItems = items.filter((it) => it.id !== id);
        const payload = serialize(name, filteredItems);

        const apiCall =
          selNote === "general_sn"
            ? updateSubCashflowApiHandler(
                {
                  groupingTemplateID: templateData._id,
                  cashflowID: selParentId,
                  subNoteID: selNoteId,
                },
                payload
              )
            : updateCashflowApiHandler(
                { groupingTemplateID: templateData._id, cashflowID: selNoteId },
                payload
              );

        apiCall
          .then((response) => {
            if (response?.data?.success === true) {
              setItems(filteredItems);
              setSuccessMessage("Successfully deleted");
              setShowSuccessPopup(true);
              getTemplateDetails();
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to delete the item",
                "error"
              );
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error ||
                error?.message ||
                "Unable to delete the item",
              "error"
            );
          });
      }
    });
  };

 const updateEditorContent = (id, html) => {
  setItems((prev) =>
    prev.map((it) =>
      it.id === id
        ? {
            ...it,
            text: [{
              id: it.text?.[0]?.id ?? genId(),
              content: html?.html || ""
            }],
            textFormat: "html",
            textRaw: html?.html ?? " ",
            textPlain: html?.text ?? " ",
          }
        : it
    )
  );
};
  const lastChangedRowInfoRef = useRef(null);
  const pendingCellFormulaUpdatesRef = useRef(new Map());

  const updateTableItem = (id, patch, rowIndex, skipRowRenameEffects = false) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id === id || it._id === id) {
          const prevRows = it.tableRow || [];
          const nextRows = patch.tableRow || [];
          let updatedCellFormulas = it.cellFormulas ? [...it.cellFormulas] : [];

          if (!skipRowRenameEffects && Array.isArray(prevRows) && Array.isArray(nextRows)) {
            prevRows.forEach((oldR, idx) => {
              const newR = nextRows[idx];
              const oldName = String(oldR?.name || "").trim();
              const newName = String(newR?.name || "").trim();

              if (oldName && newName && oldName !== newName) {
                // Update local cellFormulas
                updatedCellFormulas = updatedCellFormulas.map((f) => {
                  if (String(f?.row || "").trim() === oldName) {
                    return { ...f, row: newName };
                  }
                  return f;
                });

                if (!isConsol) {
                  (it.cellFormulas || [])
                    .filter((formula) => String(formula?.row || "").trim() === oldName)
                    .forEach((formula) => {
                      const formulaBody = {
                        row: newName,
                        col: normalizeCashflowLinkCol(formula.col),
                        type: formula.type || "formula",
                        terms: formula.terms,
                        link: formula.link,
                        isBold: formula.isBold,
                        formulaType: formula.formulaType,
                      };
                      const dataId = it._id || it.id;
                      const key = `${dataId}-${formulaBody.col}-${formulaBody.type}`;
                      pendingCellFormulaUpdatesRef.current.set(key, {
                        key,
                        params: {
                          groupingTemplateID: templateData?._id || tempId,
                          cashflowID: selNoteId,
                          dataID: dataId,
                        },
                        tableIndex: currentNote?.data?.findIndex(
                          (block) => block?._id === dataId || block?.id === dataId
                        ),
                        tableTitle: it.tableTitle,
                        body: formulaBody,
                      });
                    });
                }

                // Update linkedCells state so UI retains fx icon immediately
                setLinkedCells((prevLinked) => {
                  const nextLinked = { ...prevLinked };
                  const tId = it._id || it.id;
                  Object.keys(nextLinked).forEach((key) => {
                    const prefix = `${tId}-${oldName}-`;
                    if (key.startsWith(prefix)) {
                      const colPart = key.slice(prefix.length);
                      const newKey = `${tId}-${newName}-${colPart}`;
                      nextLinked[newKey] = {
                        ...nextLinked[key],
                        row: newName,
                      };
                    }
                  });
                  return nextLinked;
                });

              }
            });
          }

          const next = {
            ...it,
            ...patch,
            cellFormulas: updatedCellFormulas,
          };

          if (
            rowIndex != null &&
            next.tableData &&
            next.tableData[rowIndex - 1]
          ) {
            next.tableData = [...next.tableData];
            next.tableData[rowIndex - 1] = {
              ...next.tableData[rowIndex - 1],
              isEdited: true,
            };
            lastChangedRowInfoRef.current = {
              tableId: id,
              rowIndex,
              rowData: next.tableData[rowIndex - 1],
            };
          }
          return next;
        }
        return it;
      })
    );
  };

  const saveCashflowOrder = (nextItems) => {
    const groupingTemplateID = templateData?._id || tempId;
    const payload = serialize("cashflow", nextItems, { includeRowIds: true });
    const request = selNote === "general_sn"
      ? updateSubCashflowApiHandler(
          { groupingTemplateID, cashflowID: selParentId, subNoteID: selNoteId },
          payload
        )
      : updateCashflowApiHandler(
          { groupingTemplateID, cashflowID: selNoteId },
          payload
        );

    request
      .then((response) => {
        if (response?.data?.success !== true) {
          swal("Error", response?.data?.error || "Unable to save the Cashflow order", "error");
          return;
        }
        const updatedTemplate = response?.data?.updatedGroupingTemplate || response?.data?.groupingTemplate;
        if (updatedTemplate) setTemplateData(updatedTemplate);
        else getTemplateDetails();
      })
      .catch((error) => {
        swal("Error", error?.response?.data?.error || "Unable to save the Cashflow order", "error");
      });
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);
    setItems(newItems);
    saveCashflowOrder(newItems);
  };

  useEffect(() => {
    setOutput(serialize(name, items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, items]);

  useEffect(() => {
    if (isComposing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isComposing]);

  const handleAddClick = () => {
    setIsComposing(true);
    setNoteName("");
  };

  const handleCancel = () => {
    setIsComposing(false);
    setNoteName("");
  };

  const handlePost = () => {
    const value = noteName.trim();
    if (!value) return;
    setNote([value, ...note]);
    setNoteName("");
    setIsComposing(false);
  };

  const handleUpdateNote = (id) => {
    updateCashflowApiHandler(
      { groupingTemplateID: tempId, cashflowID: id },
      { name: editValue }
    )
      .then((res) => {
        if (res?.data?.success) {
          setEditingIndex(null);
          getTemplateDetails();
        }
      })
      .catch(() => {});
  };

  const handleUpdateSubNote = (parentId, subId) => {
    updateSubCashflowApiHandler(
      {
        groupingTemplateID: tempId,
        cashflowID: parentId,
        subNoteID: subId,
      },
      { name: editValue }
    )
      .then((res) => {
        if (res?.data?.success) {
          setEditingSubIndex(null);
          getTemplateDetails();
        }
      })
      .catch(() => {});
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handlePost();
    if (e.key === "Escape") handleCancel();
  };

  const [subComposeFor, setSubComposeFor] = useState(null);
  const [subNoteName, setSubNoteName] = useState("");
  const subInputRef = useRef(null);

  useEffect(() => {
    if (subComposeFor !== null && subInputRef.current) {
      subInputRef.current.focus();
    }
  }, [subComposeFor]);

  const openSubComposer = (noteIndex) => {
    setSubComposeFor(noteIndex);
    setSubNoteName("");
  };

  const cancelSubComposer = () => {
    setSubComposeFor(null);
    setSubNoteName("");
  };

  const postSubnote = () => {
    const value = subNoteName.trim();
    if (!value || subComposeFor === null) return;
    setNotes((prev) => {
      const next = [...prev];
      const target = { ...next[subComposeFor] };
      const subNotes = Array.isArray(target.subNotes)
        ? [...target.subNotes]
        : [];
      subNotes.push({ name: value });
      target.subNotes = subNotes;
      next[subComposeFor] = target;
      return next;
    });
    setSubNoteName("");
    setSubComposeFor(null);
  };

  const addSub = (note_id) => {
    const value = subNoteName.trim();
    if (!value) return;

    const generalNotesPayload = { name: subNoteName };
    createSubCashflowApiHandler(
      { groupingTemplateID: tempId, cashflowID: note_id },
      generalNotesPayload
    )
      .then((response) => {
        if (response?.data?.success === true) {
          setSubNoteName("");
          setSubComposeFor(null);
          setNotes(response?.data?.updatedGroupingTemplate?.cashflow);
        }
      })
      .catch((error) => {});
  };

  const handleSubKeyDown = (e) => {
    if (e.key === "Enter") postSubnote();
    if (e.key === "Escape") cancelSubComposer();
  };

  // ─────────────────────────────────────────────────────────────────
  // SAVE handler
  // ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (handleSave === "cashflow") {
      setHandleSave(null);
      beginCashflowSave();

      const payload = serialize(name, items);

      if (selNote === "general_sn") {
        updateSubCashflowApiHandler(
          {
            groupingTemplateID: templateData?._id || tempId,
            cashflowID: selParentId,
            subNoteID: selNoteId,
          },
          payload
        )
          .then((response) => {
            if (response?.data?.success === true) {
              setSuccessMessage("Data saved successfully");
              setShowSuccessPopup(true);
              const updatedTemplate = response?.data?.updatedGroupingTemplate || response?.data?.groupingTemplate;
              if (updatedTemplate) {
                setTemplateData(updatedTemplate);
              } else {
                getTemplateDetails();
              }
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to save the data",
                "error"
              );
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error || "Unable to save the data",
              "error"
            );
          })
          .finally(endCashflowSave);
      } else {
        updateCashflowApiHandler(
          { groupingTemplateID: templateData?._id || tempId, cashflowID: selNoteId },
          payload
        )
          .then(async (response) => {
            if (response?.data?.success === true) {
              const pendingFormulaUpdates = Array.from(
                pendingCellFormulaUpdatesRef.current.values()
              );
              pendingCellFormulaUpdatesRef.current.clear();

              try {
                if (pendingFormulaUpdates.length > 0) {
                  let updatedTemplate =
                    response?.data?.updatedGroupingTemplate || response?.data?.groupingTemplate;
                  if (!updatedTemplate?.cashflow) {
                    const templateResponse = await getTemplateDetailsApiHandler({
                      groupingTemplateID: templateData?._id || tempId,
                    });
                    updatedTemplate = templateResponse?.data?.groupingTemplate;
                  }

                  await Promise.all(
                    pendingFormulaUpdates.map((update) => {
                      const note = findCashflowNoteById(
                        updatedTemplate?.cashflow,
                        update.params.cashflowID
                      );
                      const table =
                        note?.data?.[update.tableIndex] &&
                        note.data[update.tableIndex].tableTitle === update.tableTitle
                          ? note.data[update.tableIndex]
                          : note?.data?.find(
                              (block) =>
                                block?.dataType === "table" &&
                                block?.tableTitle === update.tableTitle
                            );

                      if (!table?._id && !table?.id) {
                        throw new Error("Updated Cashflow table ID was not found");
                      }

                      update.params = {
                        ...update.params,
                        dataID: table._id || table.id,
                      };
                      return setCashflow(update.params, update.body);
                    })
                  );
                  if (updatedTemplate?.cashflow) setTemplateData(updatedTemplate);
                }
              } catch (error) {
                pendingFormulaUpdates.forEach((update) => {
                  if (!pendingCellFormulaUpdatesRef.current.has(update.key)) {
                    pendingCellFormulaUpdatesRef.current.set(update.key, update);
                  }
                });
                throw error;
              }

              setSuccessMessage("Data saved successfully");
              setShowSuccessPopup(true);
              const updatedTemplate = response?.data?.updatedGroupingTemplate || response?.data?.groupingTemplate;
              if (pendingFormulaUpdates.length > 0) {
                getTemplateDetails();
              } else if (updatedTemplate) {
                setTemplateData(updatedTemplate);
              } else {
                getTemplateDetails();
              }
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to save the data",
                "error"
              );
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error || "Unable to save the data",
              "error"
            );
          })
          .finally(endCashflowSave);
      }
    }
  }, [handleSave, selNote, selNoteId, selParentId, templateData, items, setHandleSave, name, tempId]);



  useEffect(() => {
    setNotes(templateData?.cashflow);
    if (!templateData?.cashflow) return;

    if (selNoteId) {
      let activeNote = null;
      const findNote = (arr) => {
        for (const n of arr) {
          if (n._id === selNoteId) {
            activeNote = n;
            return;
          }
          if (n.subNotes) {
            findNote(n.subNotes);
          }
        }
      };
      findNote(templateData.cashflow);

      if (activeNote) {
        setItems(normalizeFromInitial(activeNote));
      }
    }

    const links = {};
    const extractLinks = (notesArray) => {
      if (!Array.isArray(notesArray)) return;
      notesArray.forEach((note) => {
        const blocks = note.data || [];
        blocks.forEach((it) => {
          if (it.dataType === "table" && it.cellFormulas) {
            it.cellFormulas.forEach((f) => {
              buildCashflowLinkKeys(it._id, f.row, f.col).forEach((key) => {
                links[key] = f;
              });
            });
          }
        });
        if (note.subNotes) {
          extractLinks(note.subNotes);
        }
      });
    };
    extractLinks(templateData.cashflow);
    setLinkedCells(links);
  }, [templateData]);

  useEffect(() => {
    if (notes && notes.length > 0 && !selNote) {
      const firstNote = notes[0];
      const initialItems = normalizeFromInitial(firstNote);
      setItems(initialItems);
      setActiveItem(`gs_0`);
      setSelNote("general");
      setSelNoteId(firstNote._id);
      setSelParentId(null);
      setOutput(serialize(firstNote.name, initialItems));
      setName(firstNote.name || "");
    }
  }, [notes, selNote]);

  const addNotes = () => {
    const value = noteName.trim();
    if (!value) return;

    const generalNotesPayload = { name: noteName };
    createCashflowApiHandler(
      { groupingTemplateID: tempId },
      generalNotesPayload
    )
      .then((response) => {
        if (response?.data?.success === true) {
          setNoteName("");
          setIsComposing(false);
          setNotes(response?.data?.updatedGroupingTemplate?.cashflow);
        }
      })
      .catch((error) => {
        swal(
          "Error",
          error?.response?.data?.error || "Unable to save the data",
          "error"
        );
      });
  };

  const resetItems = () => {
    setItems(normalizeFromInitial(null));
    setActiveItem(null);
    setSelNote(null);
    setSelNoteId(null);
    setSelParentId(null);
  };

  const deleteNoteHandler = (id) => {
    setDeleteConfirm({
      title: "Confirm deletion of cashflow data?",
      message: "Once deleted, you will not be able to recover this!",
      onConfirm: () => {
        setDeleteConfirm(null);
        isDeletingRef.current = true;
        deleteCashflowApiHandler({
          groupingTemplateID: tempId,
          cashflowID: id,
        })
          .then((response) => {
            if (response?.data?.success) {
              resetItems();
              getTemplateDetails();
              setSuccessMessage("Successfully deleted");
              setShowSuccessPopup(true);
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to delete",
                "error"
              );
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error || "Unable to delete",
              "error"
            );
          });
      }
    });
  };

  const deleteSubNoteHandler = (id, subid) => {
    setDeleteConfirm({
      title: "Confirm deletion of cashflow data?",
      message: "Once deleted, you will not be able to recover this!",
      onConfirm: () => {
        setDeleteConfirm(null);
        isDeletingRef.current = true;
        deleteSubCashflowApiHandler({
          groupingTemplateID: tempId,
          cashflowID: id,
          subNoteID: subid,
        })
          .then((response) => {
            if (response?.data?.success) {
              resetItems();
              getTemplateDetails();
              setSuccessMessage("Successfully deleted");
              setShowSuccessPopup(true);
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to delete",
                "error"
              );
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error || "Unable to delete",
              "error"
            );
          });
      }
    });
  };

  const handleDeleteTableRow = (tableId, rowIndex, rowData) => {
    setRowToDelete({ tableId, rowIndex, rowData });
  };

 const handleAddSubRow = (tableId) => {
  if (!selNoteId) return;

  const tableItem = items.find((it) => it.id === tableId || it._id === tableId);
  const columns = tableItem?.tableColumn || [];

  const newRow = {
    name: "",
    currentYear: "0",
    previousYear: "0",
    isNewRow: true,
    isEdited: false,
    isSubRow: true,
  };

  if (columns.length > 0) {
    columns.forEach((col, idx) => {
      const key = (col.name === "Particulars" || !col.name) ? "name" : col.name;
      if (key && key !== "name") {
        newRow[key] = idx === 0 ? "" : "0";
      }
    });
  }

  const newTableRow = {
    name: "",
    lineType: "line item",
    isSubRow: true,
    isNewRow: true,
    isEdited: false,
  };

  setItems((prev) =>
    prev.map((it) => {
      if (it.id === tableId || it._id === tableId) {
        const baseTableRow = it.tableRow || [];
        const baseTableData = (it.tableData && it.tableData.length === baseTableRow.length)
          ? it.tableData
          : baseTableRow.map((r) => ({
              name: r.name || "",
              currentYear: "",
              previousYear: "",
              isSubRow: r.isSubRow || false,
            }));

        const nextTableData = [...baseTableData, newRow];
        const nextTableRow = [...baseTableRow, newTableRow];

        return {
          ...it,
          tableData: nextTableData,
          table: nextTableData,
          tableRow: nextTableRow,
          hasCellData: true,
        };
      }
      return it;
    })
  );
};

  const getRowPermissions = (rowLabel) => ({
    allowEdit: !isReadOnly,
    allowDelete: !isReadOnly,
    allowRearrange: !isReadOnly,
  });

  const adjustCashflowParticularTextareas = useCallback(() => {
    const textareas = document.querySelectorAll(
      ".cashflow-table-container table.template-table-override tr:not(.header-row) td:nth-child(1) textarea.template-edit-input"
    );
    textareas.forEach((ta) => {
      ta.style.height = "auto";
      const scrollHeight = ta.scrollHeight;
      const targetHeight = Math.min(Math.max(scrollHeight, 44), 66);
      ta.style.height = `${targetHeight}px`;
    });
  }, []);

  useEffect(() => {
    adjustCashflowParticularTextareas();
    const timer1 = setTimeout(adjustCashflowParticularTextareas, 100);
    const timer2 = setTimeout(adjustCashflowParticularTextareas, 400);

    window.addEventListener("resize", adjustCashflowParticularTextareas);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      window.removeEventListener("resize", adjustCashflowParticularTextareas);
    };
  }, [items, templateData, adjustCashflowParticularTextareas]);

  const handleParticularTextareaInput = (e) => {
    if (
      e.target &&
      e.target.tagName === "TEXTAREA" &&
      e.target.closest("tr:not(.header-row) td:first-child, tr:not(.header-row) td:nth-child(1)")
    ) {
      const ta = e.target;
      ta.style.height = "auto";
      const targetHeight = Math.min(Math.max(ta.scrollHeight, 44), 66);
      ta.style.height = `${targetHeight}px`;
    }
  };

  return (
    <>
      <style>{`
        .cashflow-table-container table.template-table-override td:first-child,
        .cashflow-table-container table.template-table-override td:nth-child(1),
        .cashflow-table-container table.template-table-override th:first-child,
        .cashflow-table-container table.template-table-override th:nth-child(1) {
          width: 350px !important;
          min-width: 350px !important;
        }

        .cashflow-table-container table.template-table-override tr:not(.header-row) td:first-child,
        .cashflow-table-container table.template-table-override tr:not(.header-row) td:nth-child(1) {
          vertical-align: top !important;
        }

        .cashflow-table-container table.template-table-override tr:not(.header-row) td:first-child > div,
        .cashflow-table-container table.template-table-override tr:not(.header-row) td:nth-child(1) > div {
          align-items: flex-start !important;
        }

        .cashflow-table-container table.template-table-override tr:not(.header-row) td:first-child > div > span[draggable],
        .cashflow-table-container table.template-table-override tr:not(.header-row) td:nth-child(1) > div > span[draggable],
        .cashflow-table-container table.template-table-override tr:not(.header-row) td:first-child > div > [class*="dragHandle"],
        .cashflow-table-container table.template-table-override tr:not(.header-row) td:nth-child(1) > div > [class*="dragHandle"],
        .cashflow-table-container table.template-table-override tr:not(.header-row) td:first-child .dragHandle,
        .cashflow-table-container table.template-table-override tr:not(.header-row) td:nth-child(1) .dragHandle {
          align-self: flex-start !important;
          margin-top: 4px !important;
        }

        .cashflow-table-container table.template-table-override tr:not(.header-row) td:first-child textarea.template-edit-input,
        .cashflow-table-container table.template-table-override tr:not(.header-row) td:nth-child(1) textarea.template-edit-input {
          min-height: 44px !important;
          max-height: 66px !important;
          height: auto !important;
          line-height: 1.4 !important;
          white-space: normal !important;
          word-break: break-word !important;
          overflow-y: auto !important;
          resize: none !important;
          field-sizing: content !important;
        }

        .cashflow-table-container table.template-table-override tr.header-row td:first-child textarea.template-edit-input,
        .cashflow-table-container table.template-table-override tr.header-row td:nth-child(1) textarea.template-edit-input {
          line-height: 1.4 !important;
          white-space: normal !important;
          word-break: break-word !important;
          resize: none !important;
        }

        .cashflow-table-container table.template-table-override tr:not(.header-row) td:first-child span,
        .cashflow-table-container table.template-table-override tr:not(.header-row) td:nth-child(1) span {
          display: -webkit-box !important;
          -webkit-line-clamp: 3 !important;
          -webkit-box-orient: vertical !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: normal !important;
          word-break: break-word !important;
          line-height: 1.4 !important;
          max-height: 66px !important;
        }

        .cashflow-table-container table.template-table-override tr:not(.header-row) td:last-child {
          vertical-align: top !important;
        }

        .cashflow-table-container table.template-table-override tr:not(.header-row) td:last-child > div {
          align-items: flex-start !important;
        }

        .cashflow-table-container table.template-table-override tr:not(.header-row) td:last-child > div > span {
          align-self: flex-start !important;
          margin-top: 4px !important;
        }
      `}</style>
      <motion.div
        initial={{ x: 500, y: 500, opacity: 0 }}
        animate={{ x: 0, y: 0, opacity: 1 }}
        exit={{ x: 0, y: 0, opacity: 0 }}
        transition={{ duration: 0.7, ease: "easeInOut" }}
        className={libcss.hierarchyLayout}
        style={{
          border: "0px",
          marginTop: "0px",
          height: "calc(100% - 45px)",
          gap: "10px",
          width: "100%",
          maxWidth: "100%",
          paddingLeft: "15px",
          paddingRight: "15px",
          paddingTop: "10px",
          margin: 0,
        }}
      >
        <div
          className={libcss.dataDivs}
          style={{
            width: "100%",
            border: "none",
            background: "none",
            padding: "0px 0px 8px 0px",
            height: "100%",
            overflowY: "auto",
            position: "relative",
          }}
        >
          {isCashflowSaving && (
            <div
              role="status"
              aria-label="Saving Cashflow changes"
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255, 255, 255, 0.72)",
              }}
            >
              <Spinner animation="border" role="presentation" style={{ color: "#205a46" }} />
            </div>
          )}
          <div className={libcss.levelCatDiv} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="right-panel-items">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ display: "grid", gap: 20 }}
                  >
                    {items.map((it, idx) => (
                      <Draggable
                        key={it.id}
                        draggableId={it.id}
                        index={idx}
                        isDragDisabled={isReadOnly}
                      >
                        {(dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            style={{
                              ...dragProvided.draggableProps.style,
                              width: "100%",
                              minWidth: "1000px",
                            }}
                          >
                            {/* Table header */}
                            {it.type === "table" && (
                              <div
                                style={{
                                  background: "#f8f9f9",
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "10px 14px",
                                  border: "1px solid #ccd5d3",
                                  borderCollapse: "collapse",
                                  borderBottom: "none",
                                  borderRadius: "10px 10px 0 0",
                                  gap: "12px",
                                }}
                              >
                                <div
                                  {...dragProvided.dragHandleProps}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={MenuIcon}
                                    alt="drag"
                                    style={{ cursor: "grab", width: 9 }}
                                  />
                                </div>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: "14px",
                                    color: "#205a46",
                                    textTransform: "uppercase",
                                    flex: 1,
                                  }}
                                >
                                  {it.tableTitle || "Table"}
                                </span>
                                {!isReadOnly && (
                                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    <PlusIcon
                                      style={{ cursor: "pointer", width: 20, height: 20 }}
                                      onClick={() => {
                                        const tId = getResolvedTableId(it);
                                        handleAddSubRow(tId);
                                        setTimeout(() => {
                                          const tableContainer = document.getElementById(`table-container-${tId}`);
                                          if (tableContainer) {
                                            const rows = tableContainer.querySelectorAll('tbody tr');
                                            if (rows.length > 0) {
                                              rows[rows.length - 1].scrollIntoView({ behavior: "smooth", block: "nearest" });
                                            }
                                          }
                                        }, 300);
                                      }}
                                    />
                                    <EditIcon
                                      style={{ cursor: "pointer", width: 16, height: 16 }}
                                      onClick={() => setForceOpenTableId(it.id)}
                                    />
                                    <DelIcon
                                      style={{
                                        cursor: "pointer",
                                        width: 16,
                                        height: 16,
                                      }}
                                      onClick={() => removeItem(it.id)}
                                    />
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Content */}
                            {it.type === "text" ? (
                              <QuillReactEditor
                                value={it.text?.[0]?.content ?? " "}
                                onChange={(content) => {
                                  updateEditorContent(it.id, content);
                                }}
                                disabled={isReadOnly}
                                dragHandleProps={dragProvided.dragHandleProps}
                                rightControl={
                                  !isReadOnly && (
                                    <DelIcon
                                      style={{
                                        cursor: "pointer",
                                        width: 16,
                                        height: 16,
                                        marginRight: 8,
                                      }}
                                      onClick={() => removeItem(it.id)}
                                    />
                                  )
                                }
                              />
                            ) : (
                              <div
                                id={`table-container-${getResolvedTableId(it)}`}
                                className="cashflow-table-container"
                                onInput={handleParticularTextareaInput}
                                onFocus={handleParticularTextareaInput}
                                style={{
                                  border: "1px solid #ccd5d3",
                                  borderRadius: "0 0 10px 10px",
                                  borderCollapse: "seperate",
                                  background: "#fff",
                                  padding: 0,
                                  position: "relative",
                                }}
                              >
                                <TemplateTableGenerator
                                  isCashflowTable={true}
                                  tableId={getResolvedTableId(it)}
                                  getRowPermissions={getRowPermissions}
                                  onDeleteRow={(rowIndex, rowData) =>
                                    handleDeleteTableRow(
                                      getResolvedTableId(it),
                                      rowIndex,
                                      rowData
                                    )
                                  }
                                  onAddRow={() => handleAddSubRow(getResolvedTableId(it))}
                                  onCellLinkClick={(r, c, rows, currentRowName) =>
                                    onCellLinkClick(getResolvedTableId(it), r, c, rows, currentRowName)
                                  }
                                  linkedCells={linkedCells}
                                  forceOpen={getResolvedTableId(it) === forceOpenTableId}
                                  // Pass showDeleteColumn so TemplateTableGenerator
                                  // renders an extra dedicated Delete column
                                  showDeleteColumn={!isReadOnly}
                                  value={{
                                    tableTitle: it.tableTitle,
                                    tableRow: it.tableRow,
                                    tableColumn: it.tableColumn,
                                    tableType: it.tableType,
                                    table: it.tableData,
                                    hasCellData: it.hasCellData,
                                  }}
                                  onCellBlur={(r, c, val) => {
                                    // Trigger auto-save when a cell is blurred (or enter pressed)
                                    setHandleSave("cashflow");
                                  }}
                                  onChange={(next, rowIndex) => {
                                    const previousOrder = (it.tableRow || []).map(
                                      (row) => String(row?._id || row?.id || row?.name || "")
                                    );
                                    const nextOrder = (next.tableRow || []).map(
                                      (row) => String(row?._id || row?.id || row?.name || "")
                                    );
                                    const sameRows = previousOrder.length === nextOrder.length &&
                                      previousOrder.every((key) => nextOrder.includes(key));
                                    const isRowReorder = sameRows && previousOrder.some(
                                      (key, index) => key !== nextOrder[index]
                                    );
                                    const patch = {
                                      tableTitle: next.tableTitle,
                                      tableRow: next.tableRow,
                                      tableColumn: next.tableColumn,
                                      hasCellData: next.hasCellData,
                                      tableData: next.tableData,
                                      tableType: next.hasCellData ? "content" : "table",
                                    };
                                    updateTableItem(
                                      it.id,
                                      patch,
                                      rowIndex,
                                      isRowReorder
                                    );
                                    if (isRowReorder) {
                                      const reorderedItems = items.map((currentItem) =>
                                        currentItem.id === it.id || currentItem._id === it.id
                                          ? { ...currentItem, ...patch }
                                          : currentItem
                                      );
                                      saveCashflowOrder(reorderedItems);
                                    }
                                  }}
                                  onAfterOpen={() => {
                                    if (forceOpenTableId === getResolvedTableId(it))
                                      setForceOpenTableId(null);
                                  }}
                                  disabled={isReadOnly}
                                  hideHeader={true}
                                  allowReorder={true}
                                  allowAddRow={true}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          {selNote && !isReadOnly && (
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                padding: 12,
                borderRadius: 12,
                marginBottom: 12,
              }}
            >
              <CirclePlus width={37} />
              <AddEditor width={140} onClick={handleAddParagraph} />
              <AddTable width={100} onClick={handleAddTable} />
            </div>
          )}

          {isAddingTable && (
            <div
              className="cashflow-table-container"
              onInput={handleParticularTextareaInput}
              onFocus={handleParticularTextareaInput}
            >
              <TemplateTableGenerator
                forceOpen={true}
                value={{}}
                isCashflowTable={true}
                onChange={(next) => {
                  const id = genId();
                  setItems((prev) => [
                    ...prev,
                    {
                      id,
                      type: "table",
                      tableTitle: next.tableTitle,
                      tableRow: next.tableRow,
                      tableColumn: next.tableColumn,
                      hasCellData: next.hasCellData,
                      tableData: next.tableData,
                      tableType: "",
                      table: [],
                    },
                  ]);
                  setIsAddingTable(false);
                }}
                onCancel={() => setIsAddingTable(false)}
              />
            </div>
          )}
          <SuccessPopup
            isOpen={showSuccessPopup}
            message={successMessage}
            onClose={() => setShowSuccessPopup(false)}
          />
        </div>
      </motion.div>

     <SidePanel
      type="cashflow"
      activeCellKey={activeCellKey}
      isTemplate={!isConsol}
      showLinkPanel={showPanel}
      setShowLinkPanel={setShowPanel}
      onApply={handleApply}
      groupingTemplateID={tempId}
      selectedCell={selectedCell}
      cashflowID={resolvedCashflowId}   
      dataID={resolvedDataId}           
      table={activeTable}
      isConsol={isConsol}
      consolID={consolID || tempId}
    />

      <DeletePopup
        isOpen={rowToDelete !== null}
        title="Are you sure?"
        message="Once deleted, you will not be able to recover this line item!"
        onConfirm={handleConfirmDelete}
        onCancel={() => setRowToDelete(null)}
      />
      <DeletePopup
        isOpen={deleteConfirm !== null}
        title={deleteConfirm?.title}
        message={deleteConfirm?.message}
        onConfirm={deleteConfirm?.onConfirm}
        onCancel={() => setDeleteConfirm(null)}
      />
    </>
  );
}
